import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {CustomFieldCell} from '../../../components/common/table/cells/customFieldCell';
import {DateCell} from '../../../components/common/table/cells/dateCell';
import {EmptyCell} from '../../../components/common/table/cells/emptyCell';
import {TextCell} from '../../../components/common/table/cells/textCell';
import {TableColumn} from '../../../components/common/table/tableColumn';
import {TableRow, TableRowRenderOptions} from '../../../components/common/table/tableRow';
import {renderCustomFieldType} from '../../../helpers/customFieldViewHelpers';
import {CustomFieldModel, CustomFieldsEnum} from '../../../models/api/customFieldModel';
import {
  computeDefaultCustomFieldIconName,
  CustomFieldsTableColumnsEnum,
  isCustomFieldDefaultField,
} from './customFieldsTableConfig';

/*
 * Props.
 */

interface CustomFieldsTableRowProps {
  customField?: CustomFieldModel;
  options: TableRowRenderOptions;
  tableColumns: ReadonlyArray<TableColumn<CustomFieldsTableColumnsEnum>>;
  isSelected: boolean;
  isInMultiSelectMode: boolean;
  isNextSiblingSelected: boolean;
  isPreviousSiblingSelected: boolean;
  onSelect: Handler;
  onMultiSelect: Handler;
  onCheckBoxToggle: HandlerOf<boolean>;
}

/*
 * Style.
 */

interface StyledTableRowProps {
  $minHeight: number;
}
const StyledTableRow = styled(TableRow)<StyledTableRowProps>`
  min-height: ${(p) => p.$minHeight}px;
`;

/*
 * Component.
 */

export const CustomFieldsTableRow: FC<CustomFieldsTableRowProps> = (props) => {
  const {
    customField,
    options,
    tableColumns,
    isSelected,
    isInMultiSelectMode,
    isPreviousSiblingSelected,
    isNextSiblingSelected,
    onCheckBoxToggle,
    onMultiSelect,
    onSelect,
  } = props;

  if (!customField) {
    return null;
  }

  const isDefaultField = isCustomFieldDefaultField(customField);

  const cellRenderer = (
    index: number,
    column: TableColumn<CustomFieldsTableColumnsEnum>,
    isHovering: boolean,
    shouldShowStickyGradient: boolean,
  ) => {
    switch (column.type) {
      case CustomFieldsTableColumnsEnum.NAME:
        return (
          <CustomFieldCell
            key={CustomFieldsTableColumnsEnum.NAME}
            value={customField}
            settings={column.settings}
            size={options.size}
            isHovering={isHovering}
            isSelected={isSelected}
            isDefaultField={isDefaultField}
            renderDefaultPillTooltip={() => (
              <Trans id="custom-fields-table-default-field-tooltip">
                This is a default field in Front. It cannot be edited or deleted.
              </Trans>
            )}
            index={index}
            avatarSize={VisualSizesEnum.LARGE}
            iconName={computeDefaultCustomFieldIconName(customField)}
            shouldShowStickyGradient={shouldShowStickyGradient}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
          />
        );
      case CustomFieldsTableColumnsEnum.TYPE:
        return (
          <TextCell
            key={CustomFieldsTableColumnsEnum.TYPE}
            value={customField.type}
            settings={column.settings}
            size={options.size}
            isHovering={isHovering}
            isSelected={isSelected}
            index={index}
            shouldShowStickyGradient={shouldShowStickyGradient}
            renderValue={(value) => renderCustomFieldType(value as CustomFieldsEnum)}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
          />
        );
      case CustomFieldsTableColumnsEnum.CREATED_DATE:
        return (
          <DateCell
            key={CustomFieldsTableColumnsEnum.CREATED_DATE}
            value={customField.createdAt}
            settings={column.settings}
            size={options.size}
            isHovering={isHovering}
            isSelected={isSelected}
            index={index}
            customFormat={{
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            }}
            shouldShowStickyGradient={shouldShowStickyGradient}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
          />
        );
      default: {
        return (
          <EmptyCell
            settings={column.settings}
            isHovering={isHovering}
            size={options.size}
            index={index}
            shouldRemoveTopBorderRadius={isPreviousSiblingSelected}
            shouldRemoveBottomBorderRadius={isNextSiblingSelected}
          />
        );
      }
    }
  };

  return (
    <StyledTableRow
      onSelect={(event) => {
        if (event.defaultPrevented || isDefaultField) {
          return;
        }
        event.preventDefault();

        // If they CMD click, treat the same as checking the checkbox.
        if (event.metaKey) {
          onCheckBoxToggle(!isSelected);
          return;
        }
        // If they shift click, issue a multi select.
        if (event.shiftKey) {
          onMultiSelect();
          return;
        }
        // If we are in multi select mode, treat this as toggling the checkbox.
        if (isInMultiSelectMode) {
          onCheckBoxToggle(!isSelected);
          return;
        }
        // Default to selecting the row.
        onSelect();
      }}
      onCheckBoxClick={(event) => {
        if (event.defaultPrevented) {
          return;
        }
        event.preventDefault();
        onCheckBoxToggle(!isSelected);
      }}
      renderColumn={(isHovering) =>
        tableColumns.map((column, index) =>
          cellRenderer(index, column, isHovering, Boolean(options.shouldShowStickyGradient)),
        )
      }
      hasHover
      $minHeight={options.tableRowHeight}
      shouldShowRightGradient={options.shouldShowRightGradient}
      isSelected={isSelected}
      isDisabled={isDefaultField}
      shouldAllowMultiSelect={options.shouldAllowMultiSelect}
      isCheckboxesVisible={isInMultiSelectMode}
    />
  );
};
